"use client";
import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar";
import Header from "../Header";
import { Edit, Plus, Trash2, X, ChevronsLeft, ChevronsRight } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useDepartments } from "../../context/DepartmentContext"; // ✅ Import context
import { useAuth } from "../../context/AuthContext";

export default function Departments() {
  const {
    departments,
    fetchDepartments,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    loading,
    error,
  } = useDepartments();
const [expandedRows, setExpandedRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalType, setModalType] = useState("create");
  const [selectedDept, setSelectedDept] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", status: "active" });

  const user = JSON.parse(localStorage.getItem("user"));

  const { canDo } = useAuth();
  

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ✅ Fetch departments initially
  useEffect(() => {
    fetchDepartments();
  }, []);

  const filteredDepartments = departments.filter((dept) => {
    const matchesStatus = filterStatus === "All" || dept.status === filterStatus;
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      dept.name?.toLowerCase().includes(search) ||
      dept.id?.toString().toLowerCase().includes(search);
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredDepartments.length / rowsPerPage);
  const indexOfLastRow = page * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredDepartments.slice(indexOfFirstRow, indexOfLastRow);

  // Handle input
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // ✅ Create/Edit submit (uses API)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modalType === "create") {
      const res = await createDepartment({
        name: form.name,
        description: form.description,
        status: form.status,
        create_audit_id: user.id, // TODO: Replace with logged-in user id
      });
      if (res.success) setShowModal(false);
    } else if (modalType === "edit" && selectedDept) {
      const res = await updateDepartment(selectedDept.id, {
        name: form.name,
        description: form.description,
        status: form.status,
        update_audit_id: user.id, // TODO: Replace with logged-in user id
      });
      if (res.success) setShowModal(false);
    }
    setForm({ name: "", description: "", status: "active" });
  };

  // ✅ Delete department (uses API)
  const handleDelete = async () => {
    const res = await deleteDepartment(selectedDept.id, user.id); // TODO: Replace with logged-in user id
    if (res.success) setShowModal(false);
  };

  const openCreateModal = () => {
    setModalType("create");
    setForm({ name: "", description: "", status: "active" });
    setShowModal(true);
  };

  const openEditModal = (dept) => {
    setModalType("edit");
    setSelectedDept(dept);
    setForm({
      name: dept.name || "",
      description: dept.description || "",
      status: dept.status || "active",
    });
    setShowModal(true);
  };

  const openDeleteModal = (dept) => {
    setModalType("delete");
    setSelectedDept(dept);
    setShowModal(true);
  };

  return (
    <>
      <div className=" bg-sky-50">
       
          <div className="px-6 py-6 sm:px-9 max-sm:px-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Departments</h1>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white rounded-xl px-3 py-3 shadow-sm">
              {/* Search + Filter */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white w-full sm:w-auto focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="outline-none text-sm flex-1 bg-transparent w-full"
                  />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border-l border-gray-300 pl-2 text-sm outline-none bg-transparent"
                  >
                    <option value="All">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {(searchTerm || filterStatus !== "All") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterStatus("All");
                    }}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
                  >
                    <X size={14} />
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Create Button */}
              {canDo("Departments", "Add") && (
             <button
  onClick={openCreateModal}
  className="flex items-center justify-center  gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium transition w-full sm:w-auto"
style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}
>
  <Plus size={15} />
  Create New Department
</button>



              )}
            </div>
  

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full border-collapse table-fixed">
                  <thead>
                 <tr className="bg-sky-100 text-gray-700">
                      <th className="py-3 px-4 text-left font-semibold w-[8%]">ID</th>
<th className="py-3 px-4 text-left font-semibold w-[22%]">Department</th>
<th className="py-3 px-4 text-left font-semibold w-[45%]">Description</th>
<th className="py-3 px-4 text-left font-semibold w-[15%]">Status</th>
                      {(canDo("Departments", "Edit") || canDo("Departments", "Delete")) && (
                        <th className="py-3 px-4 text-center font-semibold whitespace-nowrap">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length > 0 ? (
                      currentRows.map((dept, index) => (
                       <tr
  key={dept.id}
  className={`border-b text-sm hover:bg-gray-50 transition ${index % 2 === 1 ? "bg-[#f7f6fe]" : ""}`}
>
                          <td className="py-3 px-4 text-indigo-600">
  {indexOfFirstRow + index + 1}
</td>
                          <td className="py-3 px-4 text-gray-800">{dept.name}</td>
                        <td className="py-3 px-4">
  <p className="text-gray-700 break-words leading-relaxed">
    {expandedRows[dept.id]
      ? dept.description
      : dept.description?.length > 80
      ? dept.description.slice(0, 80) + "..."
      : dept.description}
  </p>

  {dept.description?.length > 80 && (
    <button
      className="text-indigo-600 text-xs mt-1 hover:underline"
      onClick={() =>
        setExpandedRows((prev) => ({
          ...prev,
          [dept.id]: !prev[dept.id],
        }))
      }
    >
      {expandedRows[dept.id] ? "Show less" : "Read more"}
    </button>
  )}
</td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                dept.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                              }`}
                            >
                              {dept.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex justify-center gap-3 text-gray-600">
                              {canDo("Departments", "Edit") && (
                              <Edit size={16} className="cursor-pointer hover:text-indigo-600" onClick={() => openEditModal(dept)} />
                              )}
                              {canDo("Departments", "Delete") && (
                              <Trash2 size={16} className="cursor-pointer hover:text-red-600" onClick={() => openDeleteModal(dept)} />
                              )}
                              </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-6 text-gray-500">
                          No departments found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <span>Rows per page:</span>
                  <div className="border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                    <select
                      className="px-2 py-1 outline-none bg-white"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      {[5, 10, 15].map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Updated Pagination Buttons */}
                <div className="flex items-center space-x-1">
                  <button
  onClick={() => setPage(1)}
  disabled={page === 1}
  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
>
  <ChevronsLeft size={20} />
</button>
                  <button
                    className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                  >
                    <MdKeyboardArrowLeft size={20} />
                  </button>

                  {(() => {
                    let startPage = Math.max(page - 1, 1);
                    let endPage = startPage + 2;
                    if (endPage > totalPages) {
                      endPage = totalPages;
                      startPage = Math.max(endPage - 2, 1);
                    }
                    const pages = [];
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            i === page
                              ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                          onClick={() => setPage(i)}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pages;
                  })()}

                  <button
                    className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                  >
                    <MdKeyboardArrowRight size={20} />
                  </button>
                  <button
  onClick={() => setPage(totalPages)}
  disabled={page === totalPages}
  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
>
  <ChevronsRight size={20} />
</button>
                </div>
              </div>
            </div>
          </div>
      </div>

      {/* Modals */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl relative max-sm:max-w-sm">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <X size={20} />
            </button>

            {modalType === "delete" ? (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Delete Department</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">{selectedDept?.name}</span>?
                </p>
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 w-full sm:w-auto"
                  >
                    Delete
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  {modalType === "create" ? "Add New Department" : "Edit Department"}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-700">Name</label>
                    <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 text-sm outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Description</label>
                    <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                      <textarea
                        name="description"
                        rows="3"
                        value={form.description}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 text-sm outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-700 mb-2 block">Status</label>
                    <div className="flex items-center gap-5">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="status"
                          value="active"
                          checked={form.status === "active"}
                          onChange={handleChange}
                        />
                        <span className="text-gray-700 text-sm">Active</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="status"
                          value="inactive"
                          checked={form.status === "inactive"}
                          onChange={handleChange}
                        />
                        <span className="text-gray-700 text-sm">Inactive</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2  text-white rounded-lg text-sm  w-full sm:w-auto"
                   style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}} >
                      {modalType === "create" ? "Create" : "Save Changes"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}